import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Alert, useMantineTheme, NumberInput, Group, Loader, ActionIcon, Table, Checkbox, Divider, Text, createStyles, Title, Space, LoadingOverlay, Button, Accordion } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
// import DeleteAccount from "../../components/delete-account/delete-account.component";
import { useUserAuth } from "../../contexts/user.context";
import { updateUserData } from "../../utils/firebase/firebase.utils";
import { showNotification } from '@mantine/notifications';
import FormPaper from "../../components/form-paper/form-paper.component";
import PaymentForm from "../../components/payment-form/payment-form.component";
import APIModal from "../../components/api-modal/api-modal.component";
import ApiUsageChart from '../../components/api-usage-charts/api-usage-charts.components';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from '@stripe/stripe-js';
import { useJuneProvider } from "../../contexts/analytics.context";
import { beigeColor } from '../../assets/constants';
import { useLocation, useNavigate } from "react-router-dom";

const useStyles = createStyles((theme) => ({
    outerContainer: {
        width: '100%',
        height: '100%',
        display: 'block',
        padding: '75px 20px',
        background: beigeColor,
        [theme.fn.smallerThan('xs')]: {
            background: 'white',
            padding: '0px',
        }
    },

    formContainer: {
        width: '100%',
        maxWidth: '650px',
        margin: '0px auto',
    },

    titleContainer: {
        paddingTop: '17.5px',
        paddingBottom: '17.5px',
        boxSizing: 'border-box',
        width: '100%',
        background: theme.colors.gray[1],
        borderRadius: '16px 16px 0px 0px',
        [theme.fn.smallerThan('xs')]: {
            background: 'white',
            paddingTop: '15px',
            paddingBottom: '0px',
        }
    },

    initialLoaderContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },

    innerPaper: {
        paddingLeft: '40px',
        paddingRight: '40px',
        paddingBottom: '40px',
        [theme.fn.smallerThan('xs')]: {
            padding: '0px',
            paddingBottom: '40px',
        }

    },

    emailTitle: {
        fontWeight: '500',
        fontSize: '20px',
    },

    fullWidthButton: {
        width: '100%'
        // float: 'right',
    },

    growButton: {
        flexGrow: '1',
    },

    rightAlignButton: {
        float: 'right'
    },

    mobileOnly: {
        [theme.fn.largerThan('xs')]: {
            display: 'none',
        }
    },

    desktopOnly: {
        [theme.fn.smallerThan('xs')]: {
            display: 'none',
        }
    },

    accountButton: {
        width: '100%',
        [theme.fn.smallerThan('xs')]: {
            width: '100%',
        }
    },

    trashIcon: {
        float: 'right',
    },

    linkText: {
        color: '#868e96',
        // Black on hover
        '&:hover': {
            color: '#212529',
        },
    },

    numberInput: {
        width: '150px',
        [theme.fn.smallerThan('xs')]: {
            width: '100%',
        }
    }

}));

const minNumCreditsPurchase = 10;
const maxNumCreditsPurchase = 20000;


const Account = () => {

    const [updatingEmailPreferences, setUpdatingEmailPreferences] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [numCreditsPurchasing, setNumCreditsPurchasing] = useState(1000);
    const [clientSecret, setClientSecret] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    const [paymentIntentId, setPaymentIntentId] = useState(null);
    const [isBuyingCredits, setIsBuyingCredits] = useState(false);
    const [paymentError, setPaymentError] = useState(false);
    const [paymentElementFirstLoad, setPaymentElementFirstLoad] = useState(true);
    const [APIKeyModalOpen, setAPIKeyModalOpen] = useState(false);
    const [deletingKeys, setDeletingKeys] = useState([]);
    const navigate = useNavigate();
    const creditsQuery = new URLSearchParams(useLocation().search).get("credits");

    const theme = useMantineTheme();
    const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`);

    const { classes } = useStyles();

    useEffect(() => {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
        setStripePromise(loadStripe(publishableKey));
        if (!creditsQuery) return;
        if (creditsQuery < minNumCreditsPurchase) setNumCreditsPurchasing(minNumCreditsPurchase)
        else if (creditsQuery > maxNumCreditsPurchase) setNumCreditsPurchasing(maxNumCreditsPurchase)
        else setNumCreditsPurchasing(Number(creditsQuery))
    }, []);

    const {
        currentUser,
        subscribeToEmails,
        setSubscribeToEmails,
        APIKeys,
        setAPIKeys,
        isLoadingUserData,
        APICreditsAvailable,
    } = useUserAuth();

    const analytics = useJuneProvider();

    const purchaseCreditsHandler = async () => {
        setPaymentError(false);
        setIsBuyingCredits(true);
        const JSON_input = JSON.stringify({ numCredits: numCreditsPurchasing, email: currentUser.email });
        const response = await fetch("/.netlify/functions/createPaymentIntent", { method: "POST", body: JSON_input });
        const responseJSON = await response.json();
        if (responseJSON.success) {
            const { paymentIntent } = responseJSON;
            const { id, client_secret } = paymentIntent;
            setClientSecret(client_secret);
            setPaymentIntentId(id);
            setPaymentModalOpen(true);
        } else {
            setIsBuyingCredits(false);
            setPaymentError(true);
        }
    }

    const deleteAPIKey = async (publicKey) => {
        setDeletingKeys([...deletingKeys, publicKey]);
        // Call netlify function to delete APIKey
        const JSON_input = JSON.stringify({ publicKey, userId: currentUser.uid });
        const response = await fetch("/.netlify/functions/deleteAPIKey", { method: "POST", body: JSON_input });
        const { success } = await response.json();
        // If success is true, remove APIKey from state
        if (success) {
            const newAPIKeys = APIKeys.filter((item) => item.publicKey !== publicKey);
            setAPIKeys(newAPIKeys);
            showNotification({
                title: 'Secret key deleted',
                color: "teal",
                style: { top: '60px' }
            });
        } else {
            showNotification({
                title: 'Something went wrong',
                color: "red",
                style: { top: '60px' }
            });
        }
        setDeletingKeys(deletingKeys.filter((item) => item !== publicKey));
    }

    const APIKeysSortedByDate = APIKeys.length > 0 ? APIKeys.sort((a, b) => a.timestamp - b.timestamp) : [];

    return (
        <>
            <Helmet>
                <title>Account | Roundtable</title>
                <meta
                    name='description'
                    content='View and manage your Roundtable account.'
                />
            </Helmet>
            <div className={classes.outerContainer}>
                <div className={classes.formContainer}>
                    <FormPaper accountPaper={true}>
                        <div className={classes.titleContainer}>
                            <Title order={1} align='center' className={classes.emailTitle}>{currentUser.email}</Title>
                        </div>
                        <div className={classes.innerPaper}>
                            {
                                stripePromise && clientSecret && (
                                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                                        <PaymentForm
                                            modalOpen={paymentModalOpen}
                                            setModalOpen={setPaymentModalOpen}
                                            numCreditsPurchasing={numCreditsPurchasing}
                                            paymentIntentId={paymentIntentId}
                                            setClientSecret={setClientSecret}
                                            setPaymentIntentId={setPaymentIntentId}
                                            setIsBuyingCredits={setIsBuyingCredits}
                                            paymentElementFirstLoad={paymentElementFirstLoad}
                                            setPaymentElementFirstLoad={setPaymentElementFirstLoad}
                                        />
                                    </Elements>
                                )
                            }
                            <APIModal
                                modalOpen={APIKeyModalOpen}
                                setModalOpen={setAPIKeyModalOpen}
                                APIKeys={APIKeys}
                                setAPIKeys={setAPIKeys}
                                currentUser={currentUser}
                            />
                            {
                                isLoadingUserData ?
                                    <>
                                        <Space h={30} />
                                        <div className={classes.initialLoaderContainer}>
                                            <Loader size={22} />
                                        </div>
                                    </>
                                    :
                                    <>
                                        <Space h='xl' />
                                        <Space h='md' />
                                        <Accordion
                                            variant="separated"
                                            defaultValue={creditsQuery ? ["APICredits"] : ["APIKeys"]}
                                            radius="md"
                                            multiple = {true}
                                        >
                                            <Accordion.Item value="APIKeys" >
                                                <Accordion.Control >API Keys</Accordion.Control>
                                                <Accordion.Panel>
                                                    <Text size='sm' color='dimmed'>
                                                        API keys are used to access the API programmatically or through Forsta.
                                                    </Text>
                                                    <Space h='md' />
                                                    {
                                                        APIKeysSortedByDate.length > 0 ?
                                                            <>
                                                                <Table>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>KEY</th>
                                                                            <th>CREATED AT</th>
                                                                            <th></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>{
                                                                        APIKeysSortedByDate.map((element) => (
                                                                            <tr key={element.publicKey}>
                                                                                <td>{element.key}</td>
                                                                                <td>{element.creationDateString}</td>
                                                                                <td>
                                                                                    <ActionIcon
                                                                                        className={classes.trashIcon}
                                                                                        size={32}
                                                                                        radius="xl"
                                                                                        onClick={() => {
                                                                                            // Check that not already deleting
                                                                                            if (!deletingKeys.includes(element.publicKey)) {
                                                                                                deleteAPIKey(element.publicKey)
                                                                                            }
                                                                                        }
                                                                                        }
                                                                                        disabled={deletingKeys.length > 0 && !deletingKeys.includes(element.publicKey)}
                                                                                        sx={{ pointerEvents: deletingKeys.includes(element.publicKey) ? 'none' : 'auto' }}
                                                                                    >
                                                                                        {
                                                                                            deletingKeys.includes(element.publicKey) ?
                                                                                                <Loader size={18} />
                                                                                                :
                                                                                                <IconTrash stroke={1.5} size={18} />

                                                                                        }
                                                                                    </ActionIcon>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </>
                                                            :
                                                            <Text size='lg' ta='center'>You don't have any API keys.</Text>
                                                    }
                                                    <Space h='md' />
                                                    <Button
                                                        size='md'
                                                        variant='outline'
                                                        className={classes.fullWidthButton}
                                                        radius='md'
                                                        leftIcon={<IconPlus stroke={1.5} size={22} />}
                                                        onClick={() =>{
                                                            if (analytics) {
                                                                analytics.track({
                                                                    userId: currentUser.uid,
                                                                    event: 'Generated API key',
                                                                });
                                                            }
                                                            setAPIKeyModalOpen(true)
                                                        }}
                                                    >
                                                        Create new API key
                                                    </Button>
                                                </Accordion.Panel>
                                            </Accordion.Item>
                                            <Accordion.Item value="APIUsage">
                                                <Accordion.Control>API Usage</Accordion.Control>
                                                <Accordion.Panel>
                                                    <ApiUsageChart userId={currentUser.uid} />
                                                </Accordion.Panel>
                                            </Accordion.Item>
                                            <Accordion.Item value="APICredits">
                                                <Accordion.Control>API Credits</Accordion.Control>
                                                <Accordion.Panel>
                                                    <Space h='md' />
                                                    <Text size='xl' ta='center'>Current Alias credit balance: {APICreditsAvailable.toLocaleString()}</Text>
                                                    <Space h='sm' />
                                                    <Group align='end'>
                                                        <NumberInput
                                                            label='Buy Alias credits'
                                                            value={numCreditsPurchasing}
                                                            onChange={(rawNumber) => {
                                                                let numCreditsToBePurchased = minNumCreditsPurchase
                                                                if (!isNaN(rawNumber)) {
                                                                    const roundedValue = Math.floor(rawNumber); // Round down to nearest integer
                                                                    if (roundedValue > maxNumCreditsPurchase) numCreditsToBePurchased = maxNumCreditsPurchase
                                                                    else if (roundedValue > minNumCreditsPurchase) numCreditsToBePurchased = roundedValue
                                                                }
                                                                setNumCreditsPurchasing(numCreditsToBePurchased)
                                                                const queryParams = new URLSearchParams();
                                                                queryParams.set('credits', numCreditsToBePurchased);
                                                                navigate({
                                                                    search: queryParams.toString(),
                                                                    replace: true 
                                                                });
                                                            }}
                                                            min={minNumCreditsPurchase}
                                                            max={maxNumCreditsPurchase}
                                                            size='md'
                                                            hideControls={isXs}
                                                            className={classes.numberInput}
                                                            sx={{ width: '150px' }}
                                                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                                            formatter = {(value) => {
                                                                if (!Number.isNaN(parseFloat(value))) {
                                                                let parts = value.toString().split(".");
                                                                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                                                return `${parts.join(".")}`;
                                                                }
                                                                return '';
                                                            }
                                                            }
                                                        />
                                                        <Button
                                                            size='md'
                                                            className={classes.growButton}
                                                            radius='md'
                                                            onClick={purchaseCreditsHandler}
                                                            disabled={isBuyingCredits}
                                                            loading={isBuyingCredits}
                                                        >
                                                            {
                                                                isBuyingCredits ? '' : `Buy ${numCreditsPurchasing.toLocaleString()} credits`
                                                            }
                                                        </Button>
                                                    </Group>
                                                    <Space h='sm' />
                                                    <Text color='dimmed' size='sm'>
                                                        Credits are $0.20 each. One credit can be used to make one <a href='https://alias-docs.roundtable.ai' target='_blank' className={classes.linkText}>Alias API</a> call.
                                                    </Text>
                                                    {
                                                        paymentError &&
                                                        <>
                                                            <Space h='md' />
                                                            <Alert title="Oops!" color="red" radius='lg'>
                                                                We've encountered a problem.
                                                                Please try again or contact us at support@roundtable.ai
                                                            </Alert>
                                                        </>
                                                    }
                                                </Accordion.Panel>
                                            </Accordion.Item>
                                            <Accordion.Item value="EmailPreferences">
                                                <Accordion.Control>Email Preferences</Accordion.Control>
                                                <Accordion.Panel>
                                                    <div style={{ position: 'relative' }}>
                                                        <LoadingOverlay
                                                            visible={updatingEmailPreferences}
                                                            overlayBlur={2}
                                                            sx={{ margin: '-5px' }}
                                                            loaderProps={{ size: 'sm'}}
                                                        />
                                                        <Checkbox
                                                            checked={subscribeToEmails}
                                                            label="Get emails about Roundtable releases (a few times a year)"
                                                            size='md'
                                                            onChange={async () => {
                                                                setUpdatingEmailPreferences(true);
                                                                const newSubscribeToEmails = !subscribeToEmails;
                                                                setSubscribeToEmails(newSubscribeToEmails);
                                                                await updateUserData(currentUser, { subscribeToEmails: newSubscribeToEmails });
                                                                showNotification({
                                                                    title: 'Your preferences have been updated',
                                                                    color: "teal",
                                                                    style: { top: '60px' }
                                                                });
                                                                setUpdatingEmailPreferences(false);
                                                            }}
                                                        />
                                                    </div>
                                                </Accordion.Panel>
                                            </Accordion.Item>
                                        </Accordion>
                                    </>
                            }
                            {/* <Space h="xl" />
                            <Divider label='Danger zone' labelPosition='center' labelProps={{ size: '18px' }} />
                            <Space h="xl" />
                            <DeleteAccount classes={classes} /> */}
                        </div>
                    </FormPaper>
                </div>
            </div>
        </>
    )
}

export default Account;